import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import './BranchAndCourses.css';
import emailjs from '@emailjs/browser';

const BranchAndCourses = () => {
  const [activeBranch, setActiveBranch] = useState(0); // Set first branch as default
  const [isChanging, setIsChanging] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    course: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false,
    message: ''
  });
  const formRef = useRef();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Handle hash navigation
    if (window.location.hash === '#contact') {
      const contactSection = document.getElementById('contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  // Function to handle branch change with animation
  const handleBranchChange = (index) => {
    if (index === activeBranch) return;
    
    setIsChanging(true);
    setTimeout(() => {
      setActiveBranch(index);
      setIsChanging(false);
    }, 300); // Match this with the CSS transition time
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input
    const sanitizedValue = value.replace(/[<>]/g, ''); // Basic XSS prevention
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const validateForm = () => {
    const { name, email, course, message } = formData;
    
    // Name validation
    if (name.length < 2 || name.length > 50) {
      throw new Error('Name must be between 2 and 50 characters');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }
    
    // Course validation
    if (!['icse', 'cbse', 'competitive'].includes(course)) {
      throw new Error('Please select a valid course');
    }
    
    // Message validation
    if (message.length < 10 || message.length > 1000) {
      throw new Error('Message must be between 10 and 1000 characters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({
      submitting: true,
      success: false,
      error: false,
      message: ''
    });

    try {
      // Validate form data
      validateForm();

      // Send email using EmailJS
      try {
        await emailjs.sendForm(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          formRef.current,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
        console.log('Email sent successfully');
      } catch (emailError) {
        console.error('EmailJS Error:', emailError);
        throw new Error('Failed to send email: ' + emailError.message);
      }

      // Send data to Google Sheets
      const timestamp = new Date().toLocaleString();
      const sheetData = {
        timestamp,
        name: formData.name,
        email: formData.email,
        course: formData.course,
        message: formData.message
      };

      console.log('Sending data to Google Sheets:', sheetData);

      // Send to Google Sheets
      try {
        const response = await fetch(process.env.REACT_APP_GOOGLE_SHEETS_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sheetData)
        });

        console.log('Google Sheets Response:', response);
      } catch (sheetsError) {
        console.error('Google Sheets Error:', sheetsError);
        throw new Error('Failed to save to Google Sheets: ' + sheetsError.message);
      }

      setFormStatus({
        submitting: false,
        success: true,
        error: false,
        message: 'Thank you for your message! We will get back to you soon.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        course: '',
        message: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({
          ...prev,
          success: false,
          message: ''
        }));
      }, 5000);

    } catch (error) {
      console.error('Form Submission Error:', error);
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: error.message || 'Sorry, there was an error sending your message. Please try again later.'
      });
    }
  };

  const branches = [
    {
      name: "HEAD OFFICE - MIRA ROAD EAST (KANAKIA ROAD)",
      established: "2016",
      address: "1st Floor, Dev Paradise, Next To Lotus Hospital, Above Pizzeria, Kanakia Road, Mira Road East, Thane - 401107",
      contact: "+91 72088 88695",
      image: "/branch1.jpg",
      branchHead: "Kaushik Tiwari",
      branchHeadContact: "+91 72088 88695"
    },
    {
      name: "MIRA ROAD EAST (SHRISHTI COMPLEX)",
      established: "2018",
      address: "Shop no 7,8 & 10, Poonam Cluster 2, Opposite Balajee Hotel, Shrishti road, Mira Road, Maharashtra 401107.",
      contact: "+91 91363-82299",
      image: "/branch2.jpg",
      branchHead: "Umesh Mukati",
      branchHeadContact: "+91 91363-82299"
    },
    {
      name: "BHAYANDAR WEST",
      established: "2014",
      address: "Shop no. 4 and 5, Janki Orchid, Opposite S.L.Porwal High School, 90ft road, Bhayandar (west), Thane: 401 101.",
      contact: "+91 72088-88694",
      image: "/branch3.jpg",
      branchHead: "Pankaj Agarwal",
      branchHeadContact: "+91 72088-88694"
    },
    {
      name: "BORIVALI WEST",
      established: "2023",
      address: "Shop No. 1, Ground Floor, Shree Sai Darshan Building, Opposite Kora Kendra Ground, S.V. Road, Borivali West, Mumbai - 400092",
      contact: "+91 91363-82299",
      image: "/branch4.jpg",
      branchHead: "Shivam Pandey",
      branchHeadContact: "+91 7045641940"
    },
    {
      name: "KANDIVALI WEST (SHANKAR LANE)",
      established: "2023",
      address: "Shop No. 7, Shankar Lane, Near Shankar Temple, Kandivali West, Mumbai, Maharashtra 400067",
      contact: "+91 8779601390",
      image: "/branch5.jpg",
      branchHead: "Bhavesh Maniar",
      branchHeadContact: "+91 8779601390"
    },
    {
      name: "KANDIVALI EAST (THAKUR VILLAGE)",
      established: "2017",
      address: "Bungalow no. 1, Gokul Concorde, Dattani Park Rd, Dattani Park, Thakur Village, Kandivali East, Mumbai, Maharashtra 400101.",
      contact: "+91 7977536385",
      image: "/branch4.jpg",
      branchHead: "Vidhi Biyani",
      branchHeadContact: "+91 7977536385"
    },
    {
      name: "KANDIVALI EAST (ASHOK NAGAR)",
      established: "2023",
      address: "Shop No. 5, Ashok Nagar, Near Ashok Cinema, Kandivali East, Mumbai, Maharashtra 400101",
      contact: "+91 9136319824",
      image: "/branch6.jpg",
      branchHead: "Raj Davda",
      branchHeadContact: "+91 8879018158"
    },
    {
      name: "KANDIVALI EAST (LOKHANDWALA)",
      established: "2024",
      address: "Shop No. 8, Lokhandwala Township, Near Lokhandwala Foundation School, Kandivali East, Mumbai, Maharashtra 400101",
      contact: "+91 9819109194",
      image: "/branch7.jpg",
      branchHead: "Kaushik Tiwari",
      branchHeadContact: "+91 7208888695"
    },
    {
      name: "KANDIVALI WEST (MAHAVIR NAGAR)",
      established: "2019",
      address: "Shop No. F-57, Xth Central Mall, opposite Axis Bank, Kandivali, Mahavir Nagar, Kandivali West, Mumbai, Maharashtra 400067.",
      contact: "+91 8779601390",
      image: "/branch5.jpg",
      branchHead: "Bhavesh Maniar",
      branchHeadContact: "+91 8779601390"
    },
    {
      name: "GOREGOAN EAST (GOKULDHAM)",
      established: "2021",
      address: "Shop No. - G-2, Gokuldham Shopping Centre, Near Gokuldham High School, Goregaon (East), Mumbai – 400063",
      contact: "+91 7977536385",
      image: "/branch6.jpg",
      branchHead: "Vidhi Biyani",
      branchHeadContact: "+91 7977536385"
    },
    {
      name: "THANE WEST (HIRANANDANI ESTATE)",
      established: "2021",
      address: "4th floor, Shop No 412, Arcadia Shopping Centre, Hiranandani Estate, Thane West, Thane – 400607",
      contact: "+91 91363 19824",
      image: "/branch7.jpg",
      branchHead: "Deep Banerjee",
      branchHeadContact: "+91 91363 19824"
    },
    {
      name: "VASAI EAST",
      established: "2023",
      address: "Shop No. 14 - 16, Regal Complex, opp. Arth Hospital, near Agarwal Circle, Agrawal Nagri, Vasant Nagari, Vasai East, Vasai-Virar, Maharashtra 401208",
      contact: "+91 8779601390",
      image: "/branch8.jpg",
      branchHead: "Varun Singh",
      branchHeadContact: "+91 8779601390"
    },
    {
      name: "GOREGAON WEST",
      established: "2025",
      address: "Corporate Corner, Mandlik Nagar, Sunder Nagar, Malad West, Mumbai, Maharashtra 400064",
      contact: "+91 91363-82299",
      image: "/branch1.jpg",
      branchHead: "Vidhi Biyani",
      branchHeadContact: "+91 91363-82299"
    },
    {
      name: "DAHISAR EAST",
      established: "2025",
      address: "Ground Floor A1/3, RASHTRIYA MAZDOOR ANAND NAGAR UNIT NO 1, BLDG NO 1 & 2 CO OP HSG SOC, SHIVAJI ROAD, ANAND NAGAR, DAHISAR EAST, MUMBAI-400068",
      contact: "+91 91363-82299",
      image: "/branch2.jpg",
      branchHead: "Shivam Pandey",
      branchHeadContact: "+91 91363-82299"
    }
  ];

  return (
    <div className="branch-and-courses-page">
      <Navbar />
      <div className="branch-courses-content">
        {/* Branches Section */}
        <section className="branches-section">
          <div className="container">
            <h2>Our Branches</h2>
            
            <div className="branch-buttons">
              {branches.map((branch, index) => (
                <button 
                  key={index} 
                  className={`branch-button ${activeBranch === index ? 'active' : ''}`}
                  onClick={() => handleBranchChange(index)}
                >
                  <span className="branch-name">{branch.name}</span>
                  <span className="branch-year">Est. {branch.established}</span>
                </button>
              ))}
            </div>
            
            <div className={`branch-details-container ${isChanging ? 'changing' : ''}`}>
              <div className="branch-details-card">
                  <div className="branch-details-header">
                    <h3>{branches[activeBranch].name}</h3>
                    <span className="branch-established">Est. {branches[activeBranch].established}</span>
                  </div>
                  
                  <div className="branch-details-content">
                    <div className="branch-map-container">
                      {branches[activeBranch].name === "HEAD OFFICE - MIRA ROAD EAST (KANAKIA ROAD)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3765.833051707147!2d72.869266!3d19.289624999999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b047344ea57d%3A0xc87fcdf6cb729f63!2sBiyani's%20Super%2030!5e0!3m2!1sen!2sin!4v1664548130769!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "MIRA ROAD EAST (SHRISHTI COMPLEX)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3766.1629608538733!2d72.86983296421587!3d19.2752782506887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b05c3f91f81b%3A0xd86830e112f92279!2sPoonam%20Estate%20Cluster%202!5e0!3m2!1sen!2sin!4v1579339448027!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "BHAYANDAR WEST" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3765.636241653873!2d72.85015671399518!3d19.298178786960804!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b032d8526c1d%3A0x1dce821b56716b35!2sBiyani's%20Super%2030!5e0!3m2!1sen!2sin!4v1579335786197!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "BORIVALI WEST" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.9897164485797!2d72.84881847372003!3d19.1965392477425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7c7c5f5b8a3%3A0x9f5b5c5c5c5c5c5c!2sShree%20Sai%20Darshan%20Building%2C%20S.V.%20Road%2C%20Borivali%20West%2C%20Mumbai%2C%20Maharashtra%20400092!5e0!3m2!1sen!2sin!4v1741201868664!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "KANDIVALI EAST (THAKUR VILLAGE)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.5816745185525!2d72.83906527372018!3d19.213465247645594!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7626fac890b%3A0xd0a8663c2b65713b!2sBiyani's%20Super%2030!5e0!3m2!1sen!2sin!4v1741201610896!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "KANDIVALI WEST (MAHAVIR NAGAR)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3768.503433150966!2d72.8682006!3d19.1732017!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7a6d8b7056b%3A0xda28c36f540c083d!2sGokuldham%20Shopping%20Center%2C%20Gokuldham%20Colony%2C%20Goregaon%2C%20Mumbai%2C%20Maharashtra%20400063!5e0!3m2!1sen!2sin!4v1664548502760!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "GOREGOAN EAST (GOKULDHAM)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3768.503433150966!2d72.8682006!3d19.1732017!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7a6d8b7056b%3A0xda28c36f540c083d!2sGokuldham%20Shopping%20Center%2C%20Gokuldham%20Colony%2C%20Goregaon%2C%20Mumbai%2C%20Maharashtra%20400063!5e0!3m2!1sen!2sin!4v1664548502760!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "THANE WEST (HIRANANDANI ESTATE)" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3766.6510711482147!2d72.98220305!3d19.25403295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7bbf28e47eca7%3A0xf23fc6ab66c4e866!2sArcadia%20Shopping%20Center%2C%20Hiranandani%20Estate%2C%20Thane%20West%2C%20Thane%2C%20Maharashtra%20400607!5e0!3m2!1sen!2sin!4v1664548630094!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "GOREGAON WEST" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.0374984234386!2d72.84772847371947!3d19.1672392477425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b6f9aed773b3%3A0x103a79c75fae90cd!2sCorporate%20Corner%2C%20Mandlik%20Nagar%2C%20Sunder%20Nagar%2C%20Malad%20West%2C%20Mumbai%2C%20Maharashtra%20400064!5e0!3m2!1sen!2sin!4v1741201868664!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "DAHISAR EAST" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.2982984234386!2d72.86072847371947!3d19.2272392477425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b6f9aed773b3%3A0x103a79c75fae90cd!2sAnand%20Nagar%2C%20Dahisar%20East%2C%20Mumbai%2C%20Maharashtra%20400068!5e0!3m2!1sen!2sin!4v1741201868664!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : branches[activeBranch].name === "VASAI EAST" ? (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3763.1812303752336!2d72.82762807372595!3d19.404574041578204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7a96e81319183%3A0x6b4be68cbc605c49!2sBiyani%E2%80%99s%20Super%2030!5e0!3m2!1sen!2sin!4v1741201868664!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      ) : (
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241316.6433229747!2d72.74109995845211!3d19.08250990056064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1695895642824!5m2!1sen!2sin"
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                      )}
                    </div>
                    
                    <div className="branch-info-columns">
                      <div className="branch-info-column">
                        <div className="info-group">
                          <h4><i className="fas fa-map-marker-alt"></i> Address</h4>
                          <p>{branches[activeBranch].address}</p>
                        </div>
                        
                        <div className="info-group">
                          <h4><i className="fas fa-phone-alt"></i> Contact</h4>
                          <a href={`tel:${branches[activeBranch].contact}`} className="branch-contact-link">
                            {branches[activeBranch].contact}
                          </a>
                        </div>
                      </div>
                      
                      <div className="branch-info-column">
                        <div className="info-group">
                          <h4><i className="fas fa-user-tie"></i> Branch Head</h4>
                          <p>{branches[activeBranch].branchHead}</p>
                          <a href={`tel:${branches[activeBranch].branchHeadContact}`} className="branch-contact-link">
                            <i className="fas fa-phone"></i> {branches[activeBranch].branchHeadContact}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </section>

        {/* Courses Section */}
        <section className="courses-section">
          <div className="container">
            <h2>Courses</h2>
            <div className="courses-grid">
              <div className="course-card">
                <div className="course-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <h3>ICSE Coaching</h3>
                <p>Comprehensive coaching for ICSE students with personalized attention and expert guidance.</p>
                <ul className="course-features">
                  <li>Regular Classes</li>
                  <li>Mock Tests</li>
                  <li>Study Materials</li>
                  <li>Parent-Teacher Meetings</li>
                </ul>
              </div>

              <div className="course-card">
                <div className="course-icon">
                  <i className="fas fa-book-reader"></i>
                </div>
                <h3>CBSE Coaching</h3>
                <p>Structured learning program for CBSE students focusing on concept clarity and exam preparation.</p>
                <ul className="course-features">
                  <li>Conceptual Learning</li>
                  <li>Practice Sessions</li>
                  <li>Revision Classes</li>
                  <li>Performance Tracking</li>
                </ul>
              </div>

              <div className="course-card">
                <div className="course-icon">
                  <i className="fas fa-trophy"></i>
                </div>
                <h3>Competitive Exams</h3>
                <p>Specialized coaching for various competitive exams with proven track record.</p>
                <ul className="course-features">
                  <li>Expert Faculty</li>
                  <li>Test Series</li>
                  <li>Strategy Sessions</li>
                  <li>Result Analysis</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact-section" className="contact-section">
          <div className="container">
            <h2>Get in Touch</h2>
            <div className="contact-grid">
              <div className="contact-info">
                <h3>Contact Information</h3>
                <p><i className="fas fa-phone"></i> +91 7977536384 </p>
                <p><i className="fas fa-envelope"></i> info@biyanis.com</p>
                <p><i className="fas fa-map-marker-alt"></i> Head Office: Mumbai, Maharashtra</p>
              </div>
              <div className="contact-form">
                <h3>Enquire Now</h3>
                <form ref={formRef} onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Course</option>
                    <option value="icse">ICSE Coaching</option>
                    <option value="cbse">CBSE Coaching</option>
                    <option value="competitive">Competitive Exams</option>
                  </select>
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={formStatus.submitting}
                    className={formStatus.submitting ? 'submitting' : ''}
                  >
                    {formStatus.submitting ? 'Sending...' : 'Send Message'}
                  </button>
                  {formStatus.message && (
                    <div className={`form-message ${formStatus.success ? 'success' : 'error'}`}>
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BranchAndCourses; 